import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import { AgentType, Agent, FrontendUser, EHR, WaitlistState, WaitlistRun, Organization } from "../types";
import { Typography, Modal, Tooltip, Button, Tabs, Tab, useMediaQuery, useTheme } from "@mui/material";
import { Link, useNavigate, useParams } from "react-router-dom";
import SignoutHeader from "../subcomponents/SignoutHeader";
import ConfigureForm from "../forms/ConfigureForm";
import { SecondaryButton } from "../subcomponents/CustomButton";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../store";
import { Colors } from "../Colors";
import { selectAgents } from "../slices/AgentSlice";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import ConversationQueue from "../components/ConversationPage";
import { usePostHog } from "posthog-js/react";
import styled from "styled-components";
import LoadingWithMessage from "../subcomponents/LoadingWithMessage";
import Loader from "../subcomponents/Loader";
import { DateTime } from "luxon";
import { deleteRun, executeRun, fetchWaitlistRuns } from "../slices/WaitlistRunsSlice";
import { Sidebar } from "../styles/GeneralStyles";
import AddIcon from "@mui/icons-material/Add";
import TimerIcon from "@mui/icons-material/Timer";
import CheckIcon from "@mui/icons-material/Check";
import { getWaitlistPatients } from "../slices/PatientSlice";
import ExportWaitlistOutcomesModal from "../forms/ExportWaitlistOutcomesModal";
import IosShareIcon from "@mui/icons-material/IosShare";
import AddWebptRunModal from "../components/modals/AddWebptRunModal";
import RecentActorsIcon from "@mui/icons-material/RecentActors";
import ViewStreamIcon from "@mui/icons-material/ViewStream";
import ViewWaitlistModal from "../forms/ViewWaitlistModal";
import PersonIcon from "@mui/icons-material/Person";
import PaginatedList from "../components/paginated-list/PaginatedList";
import Select from "../components/select/Select";
import { fetchWebptProviders } from "../slices/WebptIntegrationSlice";
import DateRangePicker from "../components/date-range-picker/DateRangePicker";
import { DateRange } from "@mui/x-date-pickers-pro";

const providerFilterEHRSet = new Set([EHR.webpt]);

const sortFieldOptions = [
  { label: "Created At", value: "createdAt" },
  { label: "Appointment Date", value: "appointmentDate" },
];

const StyledLine = styled(Box)`
  border-left: 1px solid #e0e0e0;
  height: 90px;
  align-self: center;

  @media (max-width: 750px) {
    height: auto;
    border-left: none;
    border-top: 1px solid #e0e0e0;
    width: 90%;
    margin: 15px 0;
  }
`;

export const WaitlistRunRow: React.FC<{
  run: WaitlistRun;
  executeRun?: (runId: string) => void;
  agentId: string;
  deleteRun?: (runId: string) => void;
}> = ({ run, executeRun, agentId, deleteRun }) => {
  const navigate = useNavigate();

  const runDateTime: DateTime = DateTime.fromISO(run.appointmentDate, {
    setZone: true,
  });
  const runDay = runDateTime.toFormat("EEE");
  const runDate = runDateTime.toFormat("MMM d");
  const runTime = runDateTime.toFormat(runDateTime.minute === 0 ? "h a" : "h:mm a");

  const stateColor =
    run.state === WaitlistState.accepted
      ? Colors.success
      : [WaitlistState.declined, WaitlistState.noValidPatients, WaitlistState.bookingFailed, WaitlistState.expired].includes(run.state)
        ? Colors.error
        : Colors.grey1;

  const states = {
    [WaitlistState.accepted]: "Booked",
    [WaitlistState.declined]: "Declined",
    [WaitlistState.ongoing]: "Ongoing...",
    [WaitlistState.notStarted]: "Not started",

    [WaitlistState.noValidPatients]: "No valid patients",
    [WaitlistState.bookingFailed]: "Booking failed",
    [WaitlistState.expired]: "Expired",
  };

  const status = states[run.state as WaitlistState];

  return (
    <Box
      component={Link}
      to={`/dashboard/agents/${agentId}/waitlist-runs/${run.waitlistRunId}`}
      key={run.waitlistRunId}
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        marginBottom: "20px",
        backgroundColor: Colors.textfield,
        padding: { xs: "10px 15px", sm: "20px" },
        borderRadius: "25px",
        alignItems: "start",
        cursor: "pointer",
        textDecoration: "none",
        color: "inherit",
        "&:hover": {
          backgroundColor: "rgba(255, 255, 255, 0.08)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" fontSize={"0.9rem"}>
          {runDate}, {runTime} ({runDay})
        </Typography>
        <Typography variant="body1" fontSize={"0.9rem"}>
          {run.appointmentTypeName}
        </Typography>
        <Typography variant="body1" fontSize={"0.9rem"}>
          with {run.pcpName}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "start",
        }}
      >
        <Box
          sx={{
            border: `1px solid rgba(${parseInt(stateColor.slice(1, 3), 16)}, ${parseInt(stateColor.slice(3, 5), 16)}, ${parseInt(stateColor.slice(5, 7), 16)}, 0.08)`,
            borderRadius: "15px",
            padding: "4px 10px",
            background: `rgba(${parseInt(stateColor.slice(1, 3), 16)}, ${parseInt(stateColor.slice(3, 5), 16)}, ${parseInt(stateColor.slice(5, 7), 16)}, 0.1)`,
            width: "fit-content",
          }}
        >
          <Tooltip title={run.stateJustification} arrow>
            <Typography variant="body2" color={stateColor} style={{ display: "flex", alignItems: "center" }}>
              {status}
              {run.state === WaitlistState.ongoing && (
                <>
                  <span style={{ marginLeft: 8 }}>{run.waitlistSlotIds.length}</span>
                  <PersonIcon />
                </>
              )}
            </Typography>
          </Tooltip>
        </Box>
        {run.bookedBy && (
          <React.Fragment>
            <Typography variant="body1" fontSize={"0.9rem"} mt={1}>
              Booked by:
            </Typography>
            <Typography variant="body1" fontSize={"0.9rem"} style={{ fontWeight: "bold" }}>
              {run.bookedBy.firstName} {run.bookedBy.lastName}
            </Typography>
          </React.Fragment>
        )}
        {run.state === WaitlistState.ongoing && (
          <React.Fragment>
            <Typography variant="body1" fontSize={"0.9rem"} mt={1}>
              Time elapsed:
            </Typography>
            <Typography variant="body1" fontSize={"0.9rem"} style={{ fontWeight: "bold" }}>
              {DateTime.fromISO(run.lastModified).toRelative()}
            </Typography>
          </React.Fragment>
        )}
        {run.state == "not-started" && executeRun && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              executeRun(run.waitlistRunId);
            }}
          >
            Execute Run
          </button>
        )}
      </Box>

      {process.env.REACT_APP_ENV === "development" && deleteRun && (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            deleteRun(run.waitlistRunId);
          }}
        >
          Delete Run
        </Button>
      )}
    </Box>
  );
};

const DEFAULT_PROVIDERS: Record<string, any>[] = [];

const EHRPathMap = {
  [EHR.webpt]: "webptIntegration",
} as const;

const providerFetchMap = {
  [EHR.webpt]: fetchWebptProviders,
} as const;

type SortField = "createdAt" | "appointmentDate";

const WaitlistAgentDisplay: React.FC<{ agent: Agent }> = ({ agent }) => {
  const dispatch = useAppDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const organization = useSelector((state: RootState) => state.auth.organization);
  const waitlistRunIds = useSelector((state: RootState) => state.waitlistRuns.waitlistRunIds);
  const storeWaitlistRuns = useSelector((state: RootState) => state.waitlistRuns.waitlistRuns);
  const waitlistRuns = waitlistRunIds.map((id: string) => storeWaitlistRuns[id]);
  const loadingRuns = useSelector((state: RootState) => state.waitlistRuns.fetchingRunsLoading);

  const currentEHR = organization?.integrations?.[0] || EHR.none;
  const providers = useSelector((state: RootState) => state[EHRPathMap[currentEHR as keyof typeof EHRPathMap]]?.providers || DEFAULT_PROVIDERS);

  const [exportModalOpen, setExportModalOpen] = React.useState(false);
  const [selectedProviderOngoingRuns, setSelectedProviderOngoingRuns] = React.useState("*");
  const [selectedAppointmentTypeOngoingRuns, setSelectedAppointmentTypeOngoingRuns] = React.useState("*");
  const [selectedProviderEndedRuns, setSelectedProviderEndedRuns] = React.useState("*");
  const [selectedAppointmentTypeEndedRuns, setSelectedAppointmentTypeEndedRuns] = React.useState("*");
  const [selectedSortFieldOngoingRuns, setSelectedSortFieldOngoingRuns] = React.useState<SortField>("createdAt");
  const [selectedSortFieldEndedRuns, setSelectedSortFieldEndedRuns] = React.useState<SortField>("createdAt");
  const [dateRangeEndedRuns, setDateRangeEndedRuns] = React.useState<DateRange<DateTime>>([null, null]);
  const [dateRangeOngoingRuns, setDateRangeOngoingRuns] = React.useState<DateRange<DateTime>>([null, null]);

  const [runModalOpen, setRunModalOpen] = React.useState(false);

  useEffect(() => {
    if (!user?.token) {
      return;
    }
    dispatch(
      fetchWaitlistRuns({
        token: user?.token,
        agentId: agent.agentId,
        quietly: false,
      }),
    );
    //dispatch(fetchBatchProviderAppointmentTypes({ token: user?.token, providerIds: providers.map((p) => p.id) }));
  }, [providers, user]);

  useEffect(() => {
    if (!user?.token) {
      return;
    }
    const providerFetchParams = {
      token: user?.token || "",
      organizationId: user?.organizationId || "",
    };
    dispatch(providerFetchMap[currentEHR as keyof typeof providerFetchMap]?.(providerFetchParams) as any);
  }, [currentEHR, user]);

  useEffect(() => {
    const fetchData = () => {
      if (!user?.token) {
        return;
      }
      dispatch(
        fetchWaitlistRuns({
          token: user?.token,
          agentId: agent.agentId,
          quietly: true,
        }),
      );
    };
    fetchData();
  }, [dispatch, agent.agentId]);

  const onRunClose = () => {
    setRunModalOpen(false);
  };

  const [selectedRunsTab, setSelectedRunsTab] = React.useState(0);

  const successfulRuns = waitlistRuns.filter((run) => run.state === WaitlistState.accepted);
  const failedRuns = waitlistRuns.filter((run) => [WaitlistState.declined, WaitlistState.bookingFailed, WaitlistState.expired].includes(run.state));
  const emptyRuns = waitlistRuns.filter((run) => run.state === WaitlistState.noValidPatients);
  const ongoingRuns = waitlistRuns.filter((run) => run.state === WaitlistState.ongoing);
  const notStartedRuns = waitlistRuns.filter((run) => run.state === WaitlistState.notStarted);

  const endedRuns =
    selectedRunsTab === 0
      ? successfulRuns
      : selectedRunsTab === 1
        ? failedRuns
        : selectedRunsTab === 2
          ? emptyRuns
          : waitlistRuns.filter((run) => run.state !== WaitlistState.ongoing && run.state !== WaitlistState.notStarted);

  const [selectedTab, setSelectedTab] = React.useState(0);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const { providerOptions } = useMemo(() => {
    const providerOptions = [
      {
        name: "All Providers",
        id: "*",
      },
      ...providers,
    ].map((provider) => ({
      label: provider.name,
      value: provider.id,
    }));

    return { providerOptions };
  }, [providers]);

  const filteredOngoingRuns = useMemo(() => {
    const providerFilterApplied =
      selectedProviderOngoingRuns !== "*" ? ongoingRuns.concat(notStartedRuns).filter((run) => run.ehrProviderId === selectedProviderOngoingRuns) : ongoingRuns.concat(notStartedRuns);
    const appointmentTypeFilterApplied =
      selectedAppointmentTypeOngoingRuns !== "*" ? providerFilterApplied.filter((run) => run.ehrApptTypeId === selectedAppointmentTypeOngoingRuns) : providerFilterApplied;
    const dateRangeFilterApplied =
      dateRangeOngoingRuns[0] && dateRangeOngoingRuns[1]
        ? appointmentTypeFilterApplied.filter(
            (run) => DateTime.fromISO(run.appointmentDate.split("T")[0]) >= dateRangeOngoingRuns[0]! && DateTime.fromISO(run.appointmentDate.split("T")[0]) <= dateRangeOngoingRuns[1]!,
          )
        : appointmentTypeFilterApplied;
    const sortApplied = dateRangeFilterApplied.sort((a, b) => b[selectedSortFieldOngoingRuns].localeCompare(a[selectedSortFieldOngoingRuns]));
    return sortApplied;
  }, [ongoingRuns, notStartedRuns, selectedProviderOngoingRuns, selectedAppointmentTypeOngoingRuns]);

  const filteredEndedRuns = useMemo(() => {
    const providerFilterApplied = selectedProviderEndedRuns !== "*" ? endedRuns.filter((run) => run.ehrProviderId === selectedProviderEndedRuns) : endedRuns;
    const appointmentTypeFilterApplied =
      selectedAppointmentTypeEndedRuns !== "*" ? providerFilterApplied.filter((run) => run.ehrApptTypeId === selectedAppointmentTypeEndedRuns) : providerFilterApplied;
    const dateRangeFilterApplied =
      dateRangeEndedRuns[0] && dateRangeEndedRuns[1]
        ? appointmentTypeFilterApplied.filter(
            (run) => DateTime.fromISO(run.appointmentDate.split("T")[0]) >= dateRangeEndedRuns[0]! && DateTime.fromISO(run.appointmentDate.split("T")[0]) <= dateRangeEndedRuns[1]!,
          )
        : appointmentTypeFilterApplied;
    const sortApplied = dateRangeFilterApplied.sort((a, b) => b[selectedSortFieldEndedRuns].localeCompare(a[selectedSortFieldEndedRuns]));
    return sortApplied;
  }, [endedRuns, selectedProviderEndedRuns, selectedAppointmentTypeEndedRuns]);

  const renderTabContent = (type: "outcomes" | "queue") => {
    if (!user?.token) {
      return;
    }

    if (type === "outcomes") {
      return (
        <Box sx={tabBoxStyles}>
          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ marginBottom: "15px" }}>
            <Typography variant="h6">Outcomes</Typography>
            <Box display="flex" alignItems="center">
              <Tabs value={selectedRunsTab} onChange={(e, newValue) => setSelectedRunsTab(newValue)}>
                <Tab label="Booked" style={{ textTransform: "none", color: "#FFF" }} />
                <Tab label="Declined" style={{ textTransform: "none", color: "#FFF" }} />
                <Tab label="Empty" style={{ textTransform: "none", color: "#FFF" }} />
                <Tab label="All Runs" style={{ textTransform: "none", color: "#FFF" }} />
              </Tabs>
            </Box>
            <Tooltip title="Export Waitlist Runs">
              <Button
                variant="text"
                onClick={() => setExportModalOpen(true)}
                sx={{
                  color: Colors.primary,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <IosShareIcon />
              </Button>
            </Tooltip>
          </Box>

          {loadingRuns && <LoadingWithMessage message="Loading runs..." size={40} customStyles={{ marginTop: "20px" }} />}

          <Box
            sx={{
              margin: "15px 0",
              display: "flex",
              gap: "10px",
              fontSize: "0.85rem",
            }}
          >
            {providerFilterEHRSet.has(currentEHR) && (
              <Select
                value={selectedProviderEndedRuns}
                onChange={(value) => setSelectedProviderEndedRuns(value as string)}
                options={providerOptions}
                placeholder="Filter by provider"
                responsive={false}
              />
            )}
          </Box>

          <Box
            sx={{
              margin: "15px 0",
              display: "flex",
              gap: "10px",
              fontSize: "0.85rem",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "5px",
                width: "100%",
              }}
            >
              <span>Sort by:</span>
              <Select value={selectedSortFieldEndedRuns} onChange={(value) => setSelectedSortFieldEndedRuns(value as SortField)} options={sortFieldOptions} placeholder="Sort by" responsive />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "5px",
                width: "100%",
              }}
            >
              <span>Date Range:</span>
              <DateRangePicker dateRange={dateRangeEndedRuns} onChange={setDateRangeEndedRuns} placeholder="Select date range" responsive />
            </Box>
          </Box>

          {filteredEndedRuns.length > 0 ? (
            <PaginatedList
              data={filteredEndedRuns}
              renderItem={(run) => (
                <WaitlistRunRow
                  key={run.waitlistRunId}
                  run={run}
                  executeRun={() =>
                    dispatch(
                      executeRun({
                        token: user.token!,
                        runId: run.waitlistRunId,
                      }),
                    )
                  }
                  deleteRun={() =>
                    dispatch(
                      deleteRun({
                        token: user.token!,
                        runId: run.waitlistRunId,
                      }),
                    )
                  }
                  agentId={agent.agentId}
                />
              )}
              pageSize={5}
            />
          ) : (
            <Typography variant="body1" sx={{ textAlign: "center", color: Colors.info }}>
              No waitlist runs completed yet
            </Typography>
          )}

          {/* Render ExportWaitlistOutcomesModal when exportModalOpen is true */}
          {exportModalOpen && (
            <Modal open={exportModalOpen} onClose={() => setExportModalOpen(false)} aria-labelledby="export-modal-title" aria-describedby="export-modal-description">
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "80%",
                  maxWidth: 600,
                  bgcolor: "background.paper",
                  boxShadow: 24,
                  p: 4,
                  borderRadius: 2,
                  outline: "none",
                }}
              >
                <ExportWaitlistOutcomesModal agentsSelector={false} mainAgentsFilter={[agent.agentId]} onClose={() => setExportModalOpen(false)} />
              </Box>
            </Modal>
          )}
        </Box>
      );
    } else if (type === "queue") {
      if (!user?.token) {
        return;
      }
      return (
        <Box sx={tabBoxStyles}>
          <Box display="flex" alignItems="center" sx={{ marginBottom: "15px" }}>
            <Typography variant="h6">Waitlist Run Queue</Typography>
            <Tooltip title="Create run">
              <AddIcon
                onClick={() => setRunModalOpen(true)}
                style={{
                  color: Colors.primary,
                  cursor: "pointer",
                  marginLeft: "5px",
                }}
              />
            </Tooltip>
          </Box>

          {loadingRuns && <LoadingWithMessage message="Loading runs..." size={40} customStyles={{ marginTop: "20px" }} />}
          <Box
            sx={{
              margin: "15px 0",
              display: "flex",
              gap: "10px",
              fontSize: "0.85rem",
            }}
          >
            {providerFilterEHRSet.has(currentEHR) && (
              <Select
                value={selectedProviderOngoingRuns}
                onChange={(value) => setSelectedProviderOngoingRuns(value as string)}
                options={providerOptions}
                placeholder="Filter by provider"
                responsive={false}
              />
            )}
          </Box>

          <Box
            sx={{
              margin: "15px 0",
              display: "flex",
              gap: "10px",
              fontSize: "0.85rem",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "5px",
                width: "100%",
              }}
            >
              <span>Sort by:</span>
              <Select value={selectedSortFieldOngoingRuns} onChange={(value) => setSelectedSortFieldOngoingRuns(value as SortField)} options={sortFieldOptions} placeholder="Sort by" responsive />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "5px",
                width: "100%",
              }}
            >
              <span>Date Range:</span>
              <DateRangePicker dateRange={dateRangeOngoingRuns} onChange={setDateRangeOngoingRuns} placeholder="Select date range" dropdownAlignment="right" responsive />
            </Box>
          </Box>

          {filteredOngoingRuns.length > 0 ? (
            <PaginatedList
              data={filteredOngoingRuns}
              renderItem={(run) => (
                <WaitlistRunRow
                  key={run.waitlistRunId}
                  run={run}
                  executeRun={() =>
                    dispatch(
                      executeRun({
                        token: user.token!,
                        runId: run.waitlistRunId,
                      }),
                    )
                  }
                  deleteRun={() =>
                    dispatch(
                      deleteRun({
                        token: user.token!,
                        runId: run.waitlistRunId,
                      }),
                    )
                  }
                  agentId={agent.agentId}
                />
              )}
              pageSize={5}
            />
          ) : (
            <Typography variant="body1" sx={{ textAlign: "center", color: Colors.info }}>
              No waitlist runs yet
            </Typography>
          )}
        </Box>
      );
    }
    return null;
  };

  return (
    <Box sx={{ width: "100%", color: "#FFF" }}>
      {isSmallScreen ? (
        <>
          <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)} style={{ marginBottom: "10px" }} centered>
            <Tab label="Outcomes" style={{ textTransform: "none", color: "#FFF" }} />
            <Tab label="Queue" style={{ textTransform: "none", color: "#FFF" }} />
          </Tabs>
          {renderTabContent(selectedTab === 0 ? "outcomes" : "queue")}
        </>
      ) : (
        <Box sx={{ display: "flex", gap: "20px" }}>
          {renderTabContent("outcomes")}
          {renderTabContent("queue")}
        </Box>
      )}
      <>
        <AddWebptRunModal open={!!organization?.integrations.includes(EHR.webpt) && runModalOpen} onClose={onRunClose} />
      </>
    </Box>
  );
};

// Extract common styles for each tab box
const tabBoxStyles = {
  backgroundColor: Colors.bg2,
  borderRadius: "15px",
  padding: "20px",
  width: "100%",
  height: "100%",
};

const SchedulingAgentDisplay: React.FC = () => {
  return <ConversationQueue />;
};

const AgentInfoPage: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const user: FrontendUser | null = useSelector((state: RootState) => state.auth.user);
  const organization: Organization | null = useSelector((state: RootState) => state.auth.organization);
  const agent: Agent | null = useSelector(selectAgents).find((agent) => agent.agentId === agentId) || null;
  const loading = useSelector((state: RootState) => state.agents.loading);

  const dispatch = useAppDispatch();

  const { bookedApptVolume, bookedApptRate, avgTimeToFillAppt, pocAppts, waitlistAppts, initialExamAppts } = agent || {};
  const posthog = usePostHog();

  const navigate = useNavigate();

  const [isConfigFormOpen, setIsConfigFormOpen] = useState(false);
  const [isWaitlistViewOpen, setIsWaitlistViewOpen] = useState(false);

  useEffect(() => {
    const fetchData = () => {
      if (!user?.token || !agent?.agentId) {
        return;
      }
      dispatch(
        fetchWaitlistRuns({
          token: user?.token,
          agentId: agent?.agentId!,
          quietly: true,
        }),
      );
      dispatch(getWaitlistPatients({ token: user?.token!, quietly: true }));
    };
    fetchData();
  }, [dispatch, agent?.agentId]);

  useEffect(() => {
    if (agent?.agentType === AgentType.inbound) {
      navigate(`/dashboard/agents/${agentId}/agent-conversation-history`);
    }
  }, [agent?.agentType]);

  const handleViewAgentConvo = () => {
    navigate(`/dashboard/agents/${agentId}/agent-conversation-history`);
  };

  const handleBack = () => {
    posthog?.capture("Agent Info Page - Back");
    navigate(`/dashboard/agents`);
  };

  useEffect(() => {
    if (!loading && !agent) {
      navigate("/dashboard/agents");
    }
  }, [loading, agent]);

  return agent ? (
    <Box sx={{ margin: { xs: "70px 0", lg: "0" } }}>
      <SignoutHeader />
      {/* <SecondaryButton onClick={handleBack} sx={{ display: { xs: "none", lg: "flex" }, alignItems: "center", margin: "20px 0" }}>
        <ArrowBackIcon sx={{ marginRight: "10px" }} /> Back
      </SecondaryButton> */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          mt: { xs: 5, lg: 3 },
          mb: 4,
        }}
      >
        <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 0.75 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
            <Typography variant="h4" fontWeight="700" sx={{ lineHeight: 1.1 }}>
              {agent.name}
            </Typography>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                padding: "3px 10px",
                borderRadius: "999px",
                border: `1px solid ${agent.isActive ? "rgba(0, 172, 79, 0.25)" : "rgba(220, 53, 69, 0.25)"}`,
                background: agent.isActive ? "rgba(0, 172, 79, 0.08)" : "rgba(220, 53, 69, 0.08)",
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: agent.isActive ? Colors.success : Colors.error,
                }}
              />
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 500, color: agent.isActive ? Colors.success : Colors.error, lineHeight: 1 }}>{agent.isActive ? "Active" : "Inactive"}</Typography>
            </Box>
          </Box>
          <Typography variant="body1" sx={{ color: Colors.info }}>
            Monitor how your agent is filling cancellations by reaching out to patients on the waitlist.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: "10px" }}>
          <SecondaryButton
            onClick={() => {
              posthog?.capture("[PENCILED] View waitlist modal opened", {
                organizationId: user?.organizationId,
                orgName: organization?.name,
              });
              setIsWaitlistViewOpen(true);
            }}
            sx={{ padding: { xs: "0", sm: "10px 20px" } }}
          >
            <ViewStreamIcon sx={{ marginRight: { xs: "0", sm: "7px" } }} />
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: "0.8rem", sm: "1rem" },
                display: { xs: "none", sm: "block" },
              }}
            >
              View waitlist
            </Typography>
          </SecondaryButton>
        </Box>
      </Box>

      {isWaitlistViewOpen && <ViewWaitlistModal open={isWaitlistViewOpen} onClose={() => setIsWaitlistViewOpen(false)} />}

      <Box
        sx={{
          display: "flex",
          justifyContent: { xs: "space-evenly" },
          flexDirection: { xs: "column", sm: "row" },
          borderRadius: "20px",
          margin: { xs: "10px 0px 25px 0px", md: "10px 10px 25px 10px" },
          padding: { xs: "20px 0", sm: "20px", md: "20px 0" },
          background: Colors.bg2,
          boxShadow: { xs: "0 4px 6px rgba(0, 0, 0, 0.15)", sm: "none" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: { xs: "15%", sm: "7px", lg: "15px" },
            alignItems: "center",
            marginLeft: { xs: "10%", sm: "0" },
          }}
        >
          <Box>
            <EventAvailableIcon
              sx={{
                fontSize: "5rem",
                color: "#00AC4F",
                background: "#D3FFE7",
                padding: "20px",
                borderRadius: "50%",
              }}
            />
          </Box>
          <Box>
            <Typography
              variant="body1"
              sx={{
                color: Colors.grey3,
                fontSize: { xs: "0.9rem", lg: "1rem" },
              }}
            >
              Total booked this month
            </Typography>

            <Box sx={{ display: "flex", alignItems: "flex-start", mt: 0.5 }}>
              <Typography variant="h6" sx={{ fontSize: "2.25rem", lineHeight: "1.2", color: "#FFF" }}>
                {(pocAppts || 0) + (waitlistAppts || 0) + (initialExamAppts || 0)}
              </Typography>

              <Box sx={{ ml: 2, lineHeight: 1.1 }}>
                <Typography variant="body2" sx={{ color: Colors.grey3, fontSize: "0.85rem" }}>
                  IE: {initialExamAppts || 0}
                </Typography>
                <Typography variant="body2" sx={{ color: Colors.grey3, fontSize: "0.85rem" }}>
                  POC: {pocAppts || 0}
                </Typography>
                <Typography variant="body2" sx={{ color: Colors.grey3, fontSize: "0.85rem" }}>
                  Waitlist: {waitlistAppts || 0}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        <StyledLine />
        <Box
          sx={{
            display: "flex",
            gap: { xs: "15%", sm: "7px", lg: "15px" },
            alignItems: "center",
            marginLeft: { xs: "10%", sm: "0" },
          }}
        >
          <CheckIcon
            sx={{
              fontSize: "5rem",
              color: "#0F5FC2",
              background: "#CAF1FF",
              padding: "20px",
              borderRadius: "50%",
            }}
          />
          <Box sx={{ lineHeight: "0" }}>
            <Typography
              variant="body1"
              sx={{
                color: Colors.grey3,
                fontSize: { xs: "0.9rem", lg: "1rem" },
              }}
            >
              Waitlist Booking Rate
            </Typography>
            <Typography variant="h6" sx={{ fontSize: "2.25rem", lineHeight: "1.2", color: "#FFF" }}>
              {bookedApptRate !== undefined ? (bookedApptRate * 100).toFixed(2) : "0.00"}%
            </Typography>
          </Box>
        </Box>
        <StyledLine />
        <Box
          sx={{
            display: "flex",
            gap: { xs: "15%", sm: "7px", lg: "15px" },
            alignItems: "center",
            marginLeft: { xs: "10%", sm: "0" },
          }}
        >
          <TimerIcon
            sx={{
              fontSize: "5rem",
              color: "#FFA500",
              background: "#FFEFD5",
              padding: "20px",
              borderRadius: "50%",
            }}
          />
          <Box sx={{ lineHeight: "0" }}>
            <Typography
              variant="body1"
              sx={{
                color: Colors.grey3,
                fontSize: { xs: "0.9rem", lg: "1rem" },
              }}
            >
              Average Time to Fill Cancellation
            </Typography>
            <Typography variant="h6" sx={{ fontSize: "2.25rem", lineHeight: "1.2", color: "#FFF" }}>
              {avgTimeToFillAppt?.toFixed(2)} min
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        margin={0}
        sx={{
          width: "100%",
          "& > *": {
            margin: 0,
            flex: "1 1 0",
            borderRadius: "20px",
            padding: "10px",
          },
        }}
      >
        {agent.agentType !== AgentType.waitlist && agent.agentType !== AgentType.inbound && <SchedulingAgentDisplay />}
        {agent.agentType === AgentType.waitlist && <WaitlistAgentDisplay agent={agent} />}
      </Box>

      <Modal open={isConfigFormOpen} onClose={() => setIsConfigFormOpen(false)}>
        <Sidebar className={isConfigFormOpen ? "open" : ""}>
          <ConfigureForm agent={agent} isModalOpen={isConfigFormOpen} onClose={() => setIsConfigFormOpen(false)} />
        </Sidebar>
      </Modal>
    </Box>
  ) : (
    <Box>
      <SignoutHeader />
      {loading ? (
        <Loader message="" size={60} customStyles={{ height: "100%", marginTop: "30vh" }} />
      ) : (
        <Typography variant="h2" textAlign="center">
          No agent found
        </Typography>
      )}
    </Box>
  );
};

export default AgentInfoPage;
